import React, { useMemo, useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { CATEGORIES, CATEGORY_ORDER, TEMPLATES } from "@/lib/inventory";
import { ENTITY_TYPES, allTypes, resolveType } from "@/lib/entities";
import TypeSelect from "@/components/TypeSelect";
import {
  Plus, Minus, Trash2, Sparkles, Loader2, User, Truck, Boxes, ArrowRightLeft,
  MoreVertical, LayoutTemplate, PackagePlus, CheckCircle2, X, Building2, Wand2, AlertTriangle, CornerDownLeft, Warehouse, Pencil, Package, PackageOpen, ChevronRight, Skull,
} from "lucide-react";
import { toast } from "sonner";
import { useCredits } from "@/context/CreditsContext";

export default function InventoryView({ entities, inventory, api, projectId, customTypes = [], onAddType, onReloadEntities, onReloadInventory }) {
  const { refresh: refreshCredits } = useCredits();
  const [addOwnerOpen, setAddOwnerOpen] = useState(false);
  const [ownerForm, setOwnerForm] = useState({ name: "", type: "character" });
  const [addItem, setAddItem] = useState(null); // ownerId
  const [editItem, setEditItem] = useState(null); // item being edited
  const [transfer, setTransfer] = useState(null); // item
  const [scan, setScan] = useState(null); // {loading, suggestions, owners}
  const [scanning, setScanning] = useState(false);
  const [deleteOwner, setDeleteOwner] = useState(null);
  const [cmdText, setCmdText] = useState("");
  const [cmd, setCmd] = useState(null); // {loading, results}
  const [cmdBusy, setCmdBusy] = useState(false);
  const [resetAllOpen, setResetAllOpen] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [redistOpen, setRedistOpen] = useState(false);

  // Available ammo calibres across the book + who currently holds each (for the redistribute tool).
  const ammoByCalibre = useMemo(() => {
    const norm = (s) => (s || "").toLowerCase()
      .replace(/millimetre|millimeter|mm|gauge|cal\.?|calibre|caliber|ga/g, "")
      .replace(/[^a-z0-9.]/g, "");
    const m = {};
    inventory.forEach((i) => {
      if (i.category === "weapon") return;
      const c = (i.calibre || "").trim();
      const k = norm(c);
      if (!c || !k) return;
      if (!m[k]) m[k] = { key: k, label: c, holders: new Set() };
      if ((i.quantity || 0) > 0) m[k].holders.add(i.owner_id);
    });
    return m;
  }, [inventory]);
  const hasAmmo = Object.keys(ammoByCalibre).length > 0;

  const redistribute = async (payload) => {
    const { data } = await api.post(`/projects/${projectId}/inventory/redistribute`, payload);
    if (data.applied) await onReloadInventory?.();
    return data;
  };

  const resetAllGear = async () => {
    setResetting(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/inventory/reset-all`);
      await Promise.all([onReloadInventory?.(), onReloadEntities?.()]);
      setResetAllOpen(false);
      toast.success(`Cleared ${data.items_cleared} item${data.items_cleared !== 1 ? "s" : ""} and reset every chapter's sync — start fresh from Chapter 1`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't reset loadouts");
    } finally { setResetting(false); }
  };

  const runCommand = async () => {
    if (!cmdText.trim() || cmdBusy) return;
    setCmdBusy(true);
    setCmd({ loading: true, results: [] });
    try {
      const { data } = await api.post(`/projects/${projectId}/inventory/command`, { text: cmdText, apply: false });
      refreshCredits?.();
      if (!data.results.length) { toast.info("Couldn't find any items in that note"); setCmd(null); }
      else setCmd({ loading: false, results: data.results.map((r) => ({ ...r, keep: true })) });
    } catch (e) {
      if (!e.handledOutOfCredits) toast.error(e.response?.data?.detail || "Command failed");
      setCmd(null);
    } finally {
      setCmdBusy(false);
    }
  };

  const applyCommand = async () => {
    const ops = cmd.results.filter((r) => r.keep).map((r) => ({
      owner: r.owner, owner_type: r.owner_type, action: r.action,
      name: r.name, category: r.category, quantity: r.quantity, unit: r.unit,
    }));
    const { data } = await api.post(`/projects/${projectId}/inventory/command`, { text: cmdText, apply: true, operations: ops });
    setCmd(null);
    setCmdText("");
    await Promise.all([onReloadEntities(), onReloadInventory()]);
    (data.warnings || []).forEach((w) => toast.warning(w));
    toast.success("Loadouts updated");
  };

  const ownerTypes = useMemo(
    () => allTypes(customTypes).filter((t) => t.isOwner),
    [customTypes]
  );
  const ownerTypeKeys = useMemo(() => ownerTypes.map((t) => t.key), [ownerTypes]);
  const owners = useMemo(
    () => entities.filter((e) => {
      if (e.loadout_owner === true) return true;
      if (e.loadout_owner === false) return false;
      return ownerTypeKeys.includes(e.type) || e.is_source;
    }),
    [entities, ownerTypeKeys]
  );
  const byOwner = useMemo(() => {
    const m = {};
    owners.forEach((o) => (m[o.id] = []));
    inventory.forEach((i) => { (m[i.owner_id] = m[i.owner_id] || []).push(i); });
    return m;
  }, [owners, inventory]);

  // Codex kits (types flagged is_kit, with a contents list) — offered in the Add item card.
  const kits = useMemo(() => {
    const kitKeys = (customTypes || []).filter((t) => t.is_kit).map((t) => t.key);
    return entities.filter((e) => kitKeys.includes(e.type) && (e.contents || []).length > 0);
  }, [entities, customTypes]);

  const createOwner = async () => {
    if (!ownerForm.name.trim()) return;
    await api.post(`/projects/${projectId}/entities`, { name: ownerForm.name, type: ownerForm.type });
    setAddOwnerOpen(false);
    setOwnerForm({ name: "", type: "character" });
    await onReloadEntities();
    toast.success(`${resolveType(ownerForm.type, customTypes).label} added`);
  };

  const removeOwner = async (owner) => {
    // Hide from Loadouts only — never delete the Codex entry. Its gear is kept and
    // reappears if the entry is switched back on.
    await api.put(`/entities/${owner.id}`, { loadout_owner: false });
    setDeleteOwner(null);
    await Promise.all([onReloadEntities(), onReloadInventory()]);
    toast.success(`${owner.name} removed from Loadouts (still in your Codex)`);
  };

  const toggleSource = async (owner) => {
    await api.put(`/entities/${owner.id}`, { is_source: !owner.is_source });
    await onReloadEntities();
    toast.success(owner.is_source ? `${owner.name} is no longer a stock source` : `${owner.name} set as a stock source — its counts drop as kit is drawn from it`);
  };

  const emptyOwners = useMemo(
    () => owners.filter((o) => (byOwner[o.id] || []).length === 0),
    [owners, byOwner]
  );
  // Render a group for every owner TYPE actually present (so a weapon/object toggled
  // on as a loadout owner still shows), keeping the standard owner types first.
  const ownerGroupKeys = useMemo(() => {
    const present = [...new Set(owners.map((o) => o.type))];
    const ordered = ownerTypeKeys.filter((k) => present.includes(k));
    const extra = present.filter((k) => !ownerTypeKeys.includes(k));
    return [...ordered, ...extra];
  }, [owners, ownerTypeKeys]);
  const removeEmptyOwners = async () => {
    for (const o of emptyOwners) {
      // eslint-disable-next-line no-await-in-loop
      await api.put(`/entities/${o.id}`, { loadout_owner: false });
    }
    await Promise.all([onReloadEntities(), onReloadInventory()]);
    toast.success(`Hid ${emptyOwners.length} empty owner${emptyOwners.length !== 1 ? "s" : ""} from Loadouts`);
  };

  const addItemToOwner = async (ownerId, payload) => {
    await api.post(`/projects/${projectId}/inventory`, { owner_id: ownerId, ...payload });
    await onReloadInventory();
  };

  const updateItem = async (id, patch) => {
    await api.put(`/inventory/${id}`, patch);
    await onReloadInventory();
  };
  const deleteItem = async (id) => { await api.delete(`/inventory/${id}`); await onReloadInventory(); };

  const step = async (item, delta) => {
    const q = Math.max(0, (item.quantity || 0) + delta);
    await updateItem(item.id, { quantity: q });
  };

  const applyTemplate = async (ownerId, key) => {
    const tpl = TEMPLATES[key];
    for (const it of tpl.items) {
      // eslint-disable-next-line no-await-in-loop
      await api.post(`/projects/${projectId}/inventory`, { owner_id: ownerId, ...it });
    }
    await onReloadInventory();
    toast.success(`${tpl.label} added`);
  };

  const doTransfer = async (toOwnerId) => {
    const items = await api.post(`/inventory/${transfer.id}/transfer`, { to_owner_id: toOwnerId });
    setTransfer(null);
    await onReloadInventory();
    toast.success("Item transferred");
    return items;
  };

  const runScan = async () => {
    // scan across all chapters' content
    setScanning(true);
    setScan({ loading: true, suggestions: [] });
    try {
      const { data: chapters } = await api.get(`/projects/${projectId}/chapters`);
      const text = chapters.map((c) => c.content).join("\n\n");
      const { data } = await api.post(`/projects/${projectId}/inventory/scan`, { text });
      refreshCredits?.();
      setScan({ loading: false, suggestions: data.suggestions.map((s) => ({ ...s, keep: true })) });
      if (!data.suggestions.length) toast.info("No supplies detected in your chapters yet");
    } catch (e) {
      if (!e.handledOutOfCredits) toast.error(e.response?.data?.detail || "AI supply scan failed");
      setScan(null);
    } finally {
      setScanning(false);
    }
  };

  const applyScan = async () => {
    const chosen = scan.suggestions.filter((s) => s.keep);
    // match owner by name, create if missing
    const nameToId = {};
    owners.forEach((o) => (nameToId[o.name.toLowerCase()] = o.id));
    let createdAny = false;
    for (const s of chosen) {
      let ownerId = nameToId[s.owner.toLowerCase()];
      if (!ownerId) {
        // eslint-disable-next-line no-await-in-loop
        const { data } = await api.post(`/projects/${projectId}/entities`, { name: s.owner, type: s.owner_type === "vehicle" ? "vehicle" : "character" });
        ownerId = data.id;
        nameToId[s.owner.toLowerCase()] = ownerId;
        createdAny = true;
      }
      // eslint-disable-next-line no-await-in-loop
      await api.post(`/projects/${projectId}/inventory`, {
        owner_id: ownerId, name: s.name, category: s.category, quantity: s.quantity, unit: s.unit,
      });
    }
    setScan(null);
    if (createdAny) await onReloadEntities();
    await onReloadInventory();
    toast.success(`${chosen.length} supplies added to loadouts`);
  };

  return (
    <div className="flex-1 overflow-hidden flex flex-col">
      <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-border gap-3 flex-wrap">
        <div>
          <h2 className="font-display text-3xl font-bold flex items-center gap-2">
            <Boxes className="w-7 h-7 text-primary" /> Loadouts
          </h2>
          <p className="text-sm text-muted-foreground">Track items, ammo, food, water & fuel per character and vehicle.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {emptyOwners.length > 0 && (
            <Button variant="ghost" onClick={removeEmptyOwners} data-testid="remove-empty-owners-button"
              className="gap-2 text-muted-foreground hover:text-destructive">
              <Trash2 className="w-4 h-4" /> Clear {emptyOwners.length} empty
            </Button>
          )}
          {inventory.length > 0 && (
            <Button variant="ghost" onClick={() => setResetAllOpen(true)} data-testid="reset-all-gear-button"
              className="gap-2 text-muted-foreground hover:text-destructive">
              <AlertTriangle className="w-4 h-4" /> Reset all
            </Button>
          )}
          <Button variant="outline" onClick={runScan} disabled={scanning} data-testid="scan-supplies-button" className="gap-2">
            {scanning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} AI: Scan for supplies
          </Button>
          {hasAmmo && (
            <Button variant="outline" onClick={() => setRedistOpen(true)} data-testid="redistribute-ammo-button" className="gap-2">
              <ArrowRightLeft className="w-4 h-4" /> Redistribute ammo
            </Button>
          )}
          <Button onClick={() => setAddOwnerOpen(true)} data-testid="add-owner-button" className="gap-2">
            <Plus className="w-4 h-4" /> Add owner
          </Button>
        </div>
      </div>

      <div className="px-4 sm:px-6 py-3 border-b border-border bg-secondary/20">
        <div className="flex items-center gap-2 max-w-3xl">
          <Wand2 className="w-4 h-4 text-primary shrink-0" />
          <input
            value={cmdText}
            onChange={(e) => setCmdText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") runCommand(); }}
            data-testid="quick-command-input"
            placeholder='Try: "grabbed 4 water bottles and 24 arrows", "fired 30 rounds from the rifle" or "loosed a dozen arrows"'
            className="flex-1 bg-background border border-border rounded-lg px-3 py-2 text-sm outline-none focus:border-primary/60"
          />
          <Button onClick={runCommand} disabled={cmdBusy || !cmdText.trim()} data-testid="run-command-button" className="gap-2 shrink-0">
            {cmdBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CornerDownLeft className="w-4 h-4" />}
            <span className="hidden sm:inline">Apply note</span>
          </Button>
        </div>
        <p className="text-[11px] text-muted-foreground mt-1.5 ml-6">
          Smart loadouts — describe what a character/vehicle/building gained or used, and I'll update their supplies (with low-stock warnings).
        </p>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-4 sm:p-6 space-y-8">
          {owners.length === 0 ? (
            <div className="text-center py-20 max-w-md mx-auto">
              <Boxes className="w-12 h-12 text-muted-foreground/40 mx-auto mb-4" />
              <h3 className="font-display text-2xl font-bold mb-2">No owners yet</h3>
              <p className="text-muted-foreground mb-5">
                Add a character or vehicle to start tracking their supplies — or run an AI scan of your chapters.
              </p>
              <Button onClick={() => setAddOwnerOpen(true)} className="gap-2"><Plus className="w-4 h-4" /> Add your first owner</Button>
            </div>
          ) : (
            ownerGroupKeys.map((groupType) => {
              const group = owners.filter((o) => o.type === groupType);
              if (!group.length) return null;
              const tmeta = resolveType(groupType, customTypes);
              const GIcon = tmeta.icon;
              return (
                <section key={groupType} data-testid={`owner-group-${groupType}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <GIcon className="w-4 h-4 text-muted-foreground" />
                    <h3 className="text-xs uppercase tracking-widest font-semibold text-muted-foreground">
                      {tmeta.plural}
                    </h3>
                    <span className="text-xs text-muted-foreground">({group.length})</span>
                    <div className="flex-1 h-px bg-border ml-2" />
                  </div>
                  <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
                    {group.map((owner) => (
                      <OwnerCard key={owner.id} owner={owner} items={byOwner[owner.id] || []} customTypes={customTypes}
                        onAddItem={() => setAddItem(owner.id)} onStep={step} onUpdate={updateItem}
                        onDelete={deleteItem} onTransfer={setTransfer} onApplyTemplate={applyTemplate}
                        onEdit={setEditItem}
                        onToggleSource={() => toggleSource(owner)}
                        onDeleteOwner={() => setDeleteOwner(owner)} />
                    ))}
                  </div>
                </section>
              );
            })
          )}
        </div>
      </ScrollArea>

      {/* Reset all loadouts + sync ledgers */}
      <AlertDialog open={resetAllOpen} onOpenChange={setResetAllOpen}>
        <AlertDialogContent className="bg-popover" data-testid="reset-all-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl">Reset all loadouts?</AlertDialogTitle>
            <AlertDialogDescription>
              This clears <span className="font-semibold text-foreground">every character's loadout</span> and wipes the
              per-chapter Gear Sync memory for the whole book — giving you a clean slate. Use this if the numbers have
              drifted. Afterwards, sync your chapters in order from the start. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="reset-all-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); resetAllGear(); }} disabled={resetting}
              data-testid="reset-all-confirm" className="bg-destructive text-destructive-foreground hover:bg-destructive/90 gap-2">
              {resetting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Reset everything
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>


      {/* Add owner */}
      <Dialog open={addOwnerOpen} onOpenChange={setAddOwnerOpen}>
        <DialogContent className="bg-popover" data-testid="add-owner-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Add an owner</DialogTitle>
            <DialogDescription>Characters and vehicles can each carry their own loadout.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Name</Label>
              <Input value={ownerForm.name} onChange={(e) => setOwnerForm({ ...ownerForm, name: e.target.value })}
                placeholder="Mr Bloggs / The Rust Bucket" data-testid="owner-name-input" autoFocus />
            </div>
            <div className="space-y-1.5">
              <Label>Type</Label>
              <TypeSelect value={ownerForm.type} onChange={(v) => setOwnerForm({ ...ownerForm, type: v })}
                types={ownerTypes} onAddType={onAddType} ownerContext testid="owner-type-select" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={createOwner} disabled={!ownerForm.name.trim()} data-testid="create-owner-button">Add owner</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AddItemDialog open={!!addItem} onOpenChange={(v) => !v && setAddItem(null)} kits={kits}
        onAdd={async (payload) => { await addItemToOwner(addItem, payload); }} />

      <EditItemDialog item={editItem} onOpenChange={(v) => !v && setEditItem(null)}
        onSave={async (patch) => { await updateItem(editItem.id, patch); setEditItem(null); toast.success("Item updated"); }} />

      <TransferDialog item={transfer} owners={owners} onOpenChange={(v) => !v && setTransfer(null)} onTransfer={doTransfer} />

      <RedistributeDialog open={redistOpen} onOpenChange={setRedistOpen} owners={owners}
        ammoByCalibre={ammoByCalibre} onRun={redistribute} />

      <ScanDialog scan={scan} setScan={setScan} onApply={applyScan} />

      <CommandDialog cmd={cmd} setCmd={setCmd} onApply={applyCommand} />

      <AlertDialog open={!!deleteOwner} onOpenChange={(v) => !v && setDeleteOwner(null)}>
        <AlertDialogContent className="bg-popover">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove "{deleteOwner?.name}" from Loadouts?</AlertDialogTitle>
            <AlertDialogDescription>
              This hides {deleteOwner?.name} from the Loadouts view. Its gear is kept and reappears if you
              switch "Use in Loadouts" back on from the Codex. Your Codex entry is <strong>not</strong> deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => removeOwner(deleteOwner)} data-testid="confirm-delete-owner"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Remove from Loadouts</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function OwnerCard({ owner, items, customTypes, onAddItem, onStep, onUpdate, onDelete, onTransfer, onApplyTemplate, onEdit, onToggleSource, onDeleteOwner }) {
  const meta = resolveType(owner.type, customTypes);
  const OwnerIcon = meta.icon;
  const kia = !!owner.kia;
  const grouped = useMemo(() => {
    const m = {};
    items.forEach((i) => { (m[i.category] = m[i.category] || []).push(i); });
    return m;
  }, [items]);

  return (
    <div data-testid={`owner-card-${owner.id}`}
      className={`rounded-2xl border bg-card flex flex-col overflow-hidden ${kia ? "border-destructive/30 opacity-70" : "border-border"}`}>
      <div className="flex items-center gap-3 p-4 border-b border-border">
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${kia ? "grayscale" : ""}`}
          style={{ backgroundColor: meta.color + "26", color: meta.color }}>
          <OwnerIcon style={{ width: 20, height: 20 }} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <h3 className="font-display text-xl font-bold truncate leading-none">{owner.name}</h3>
            {kia && (
              <span data-testid={`owner-kia-badge-${owner.id}`} title="Marked KIA — frozen, excluded from Gear Sync"
                className="inline-flex items-center gap-0.5 text-[10px] uppercase tracking-wider font-semibold text-destructive border border-destructive/30 bg-destructive/15 rounded px-1.5 py-0.5">
                <Skull className="w-3 h-3" /> KIA
              </span>
            )}
          </div>
          {(owner.nickname || "").trim() && (
            <span className="block text-[11px] text-primary/80 truncate mt-0.5" data-testid={`owner-aka-${owner.id}`} title={`Also known as ${owner.nickname}`}>
              aka {owner.nickname}
            </span>
          )}
          <span className="text-xs text-muted-foreground">
            {meta.label} · {items.length} item{items.length !== 1 ? "s" : ""}
            {kia ? <span className="ml-1.5 text-destructive">· frozen</span>
              : items.length === 0 && <span className="ml-1.5 text-amber-500">· empty</span>}
            {owner.is_source && <span className="ml-1.5 inline-flex items-center gap-0.5 text-primary" data-testid={`owner-source-badge-${owner.id}`}><Warehouse className="w-3 h-3" /> stock source</span>}
          </span>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="text-muted-foreground hover:text-foreground" data-testid={`owner-menu-${owner.id}`}><MoreVertical className="w-4 h-4" /></button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-popover">
            {!kia && <>
              <DropdownMenuLabel className="text-xs flex items-center gap-1.5"><LayoutTemplate className="w-3.5 h-3.5" /> Apply template</DropdownMenuLabel>
              {Object.entries(TEMPLATES).map(([k, t]) => (
                <DropdownMenuItem key={k} onClick={() => onApplyTemplate(owner.id, k)} data-testid={`template-${k}-${owner.id}`}>{t.label}</DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onToggleSource} data-testid={`toggle-source-${owner.id}`}>
                <Warehouse className="w-4 h-4 mr-2" /> {owner.is_source ? "Unset stock source" : "Mark as stock source"}
              </DropdownMenuItem>
            </>}
            <DropdownMenuItem onClick={onDeleteOwner} data-testid={`delete-owner-${owner.id}`}
              className="text-destructive focus:text-destructive">
              <Trash2 className="w-4 h-4 mr-2" /> Remove from Loadouts
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="p-3 space-y-3 flex-1">
        {kia && items.length > 0 && (
          <p className="text-[11px] text-destructive/80 flex items-center gap-1.5" data-testid={`loot-hint-${owner.id}`}>
            <Skull className="w-3 h-3" /> Frozen — take their gear onto the living, or drop it.
          </p>
        )}
        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6">
            {kia ? "No gear recorded when this entry was frozen." : "Empty loadout. Add items or apply a template."}
          </p>
        ) : (
          CATEGORY_ORDER.filter((c) => grouped[c]?.length).map((cat) => {
            const CatIcon = CATEGORIES[cat].icon;
            return (
              <div key={cat}>
                <div className="flex items-center gap-1.5 mb-1.5">
                  <CatIcon className="w-3.5 h-3.5" style={{ color: CATEGORIES[cat].color }} />
                  <span className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">{CATEGORIES[cat].label}</span>
                </div>
                <div className="space-y-1.5">
                  {grouped[cat].map((item) => (
                    <ItemRow key={item.id} item={item} loot={kia} onStep={onStep} onUpdate={onUpdate} onDelete={onDelete} onTransfer={onTransfer} onEdit={onEdit} />
                  ))}
                </div>
              </div>
            );
          })
        )}
      </div>

      {!kia && (
        <button onClick={onAddItem} data-testid={`add-item-${owner.id}`}
          className="m-3 mt-0 rounded-lg border border-dashed border-border hover:border-primary/50 text-muted-foreground hover:text-primary transition-colors py-2 text-sm flex items-center justify-center gap-1.5">
          <PackagePlus className="w-4 h-4" /> Add item
        </button>
      )}
    </div>
  );
}

function ItemRow({ item, loot, onStep, onUpdate, onDelete, onTransfer, onEdit }) {
  const low = item.quantity > 0 && item.quantity <= 5;
  const empty = item.quantity <= 0;
  const isKit = item.category === "kit" && (item.contents || []).length > 0;
  const [openKit, setOpenKit] = useState(false);

  if (loot) {
    return (
      <div data-testid={`loot-row-${item.id}`} className="rounded-lg bg-secondary/40 flex items-center gap-2 px-2.5 py-1.5">
        <span className="flex-1 min-w-0 text-sm truncate flex items-center gap-1.5">
          {item.name}
          {item.category === "ammo" && Number(item.capacity) > 1 && item.quantity > 0 && (
            <span className="text-[10px] text-primary/70 font-mono-code">
              {Number.isInteger(item.quantity) ? item.quantity : Number(item.quantity).toFixed(1)} mag{item.quantity === 1 ? "" : "s"}
            </span>
          )}
        </span>
        <span className="text-sm font-mono-code font-semibold shrink-0" data-testid={`loot-qty-${item.id}`}>
          ×{Number.isInteger(item.quantity) ? item.quantity : Number(item.quantity).toFixed(1)}
        </span>
        <button onClick={() => onTransfer(item)} title="Take onto another character" data-testid={`loot-take-${item.id}`}
          className="text-xs px-2 py-1 rounded-md border border-border hover:border-primary/60 hover:text-primary flex items-center gap-1 shrink-0">
          <ArrowRightLeft className="w-3.5 h-3.5" /> Take
        </button>
        <button onClick={() => onDelete(item.id)} title="Drop / discard" data-testid={`loot-drop-${item.id}`}
          className="text-muted-foreground hover:text-destructive shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
      </div>
    );
  }

  const stepContent = (idx, delta) => {
    const contents = (item.contents || []).map((c, i) => {
      if (i !== idx) return c;
      const qty = c.qty || 0;
      const used = Math.min(qty, Math.max(0, (c.used || 0) + delta));
      return { ...c, used };
    });
    onUpdate(item.id, { contents });
  };

  return (
    <div data-testid={`item-row-${item.id}`} className={`rounded-lg ${isKit ? "bg-emerald-500/[0.06] border border-emerald-500/25" : "bg-secondary/40"}`}>
      <div className="group flex items-center gap-2 px-2.5 py-1.5">
        <span className="flex-1 min-w-0 text-sm truncate flex items-center gap-1.5">
          {isKit ? (
            <button onClick={() => setOpenKit((v) => !v)} data-testid={`kit-toggle-${item.id}`}
              title={openKit ? "Collapse contents" : "Tap to see contents"}
              className="flex items-center gap-1.5 min-w-0 hover:opacity-80 transition-opacity">
              <ChevronRight className={`w-3.5 h-3.5 text-emerald-500 shrink-0 transition-transform ${openKit ? "rotate-90" : ""}`} />
              <Package className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
              <span className="truncate font-medium">{item.name}</span>
              <span className="text-[9px] uppercase font-bold px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-500 shrink-0">
                kit · {item.contents.length}
              </span>
            </button>
          ) : (
            item.name
          )}
          {(item.calibre || item.feeds || item.capacity) && (
            <span className="text-[9px] px-1.5 py-0.5 rounded bg-primary/10 text-primary/80 font-medium truncate max-w-[9rem]"
              data-testid={`item-ammo-link-${item.id}`}
              title={[item.calibre, item.feeds && `feeds ${item.feeds}`, item.capacity && `${item.capacity} rds`].filter(Boolean).join(" · ")}>
              {[item.calibre, item.capacity ? `${Number(item.capacity)}rd` : "", item.feeds].filter(Boolean).join(" · ")}
            </span>
          )}
          {empty && <span data-testid={`item-badge-${item.id}`} className="text-[9px] uppercase font-bold px-1.5 py-0.5 rounded bg-destructive/15 text-destructive">Empty</span>}
          {low && <span data-testid={`item-badge-${item.id}`} className="text-[9px] uppercase font-bold px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-600 dark:text-amber-300 flex items-center gap-0.5"><AlertTriangle className="w-2.5 h-2.5" />Low</span>}
          {item.category === "ammo" && Number(item.capacity) > 1 && item.quantity > 0 && (
            <span data-testid={`item-rounds-${item.id}`} className="text-[10px] text-primary/70 font-mono-code" title="Total rounds = magazines × mag size">
              {Number.isInteger(item.quantity) ? item.quantity : Number(item.quantity).toFixed(1)} mag{item.quantity === 1 ? "" : "s"} · {Math.round(item.quantity * Number(item.capacity))} rds
            </span>
          )}
        </span>
        <div className="flex items-center gap-1 shrink-0">
          <button onClick={() => onStep(item, -1)} data-testid={`item-dec-${item.id}`}
            className="w-6 h-6 rounded-md bg-background border border-border flex items-center justify-center hover:border-primary/50"><Minus className="w-3 h-3" /></button>
          <span className="min-w-[2.5rem] text-center text-sm font-mono-code font-semibold" data-testid={`item-qty-${item.id}`}>
            {Number.isInteger(item.quantity) ? item.quantity : Number(item.quantity).toFixed(1)}
          </span>
          <button onClick={() => onStep(item, 1)} data-testid={`item-inc-${item.id}`}
            className="w-6 h-6 rounded-md bg-background border border-border flex items-center justify-center hover:border-primary/50"><Plus className="w-3 h-3" /></button>
          {item.unit && <span className="text-xs text-muted-foreground w-12 truncate">{item.unit}</span>}
        </div>
        <div className="flex items-center gap-1 opacity-60 group-hover:opacity-100 transition-opacity shrink-0">
          <button onClick={() => onEdit(item)} title="Edit / link ammo" data-testid={`item-edit-${item.id}`}
            className="text-muted-foreground hover:text-primary"><Pencil className="w-3.5 h-3.5" /></button>
          <button onClick={() => onTransfer(item)} title="Transfer" data-testid={`item-transfer-${item.id}`}
            className="text-muted-foreground hover:text-primary"><ArrowRightLeft className="w-3.5 h-3.5" /></button>
          <button onClick={() => onDelete(item.id)} title="Remove" data-testid={`item-delete-${item.id}`}
            className="text-muted-foreground hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      </div>
      {isKit && openKit && (
        <div className="px-3 pb-2 pt-0.5 space-y-1 border-t border-border/50" data-testid={`kit-contents-${item.id}`}>
          {item.contents.map((c, i) => {
            const remaining = (c.qty || 0) - (c.used || 0);
            return (
              <div key={i} className="flex items-center gap-2 text-xs">
                <span className={`flex-1 truncate ${remaining <= 0 ? "line-through text-muted-foreground" : ""}`}>{c.name}</span>
                <button onClick={() => stepContent(i, 1)} data-testid={`kit-use-${item.id}-${i}`}
                  className="w-5 h-5 rounded bg-background border border-border grid place-items-center hover:border-primary/50"><Minus className="w-3 h-3" /></button>
                <span className={`min-w-[2.5rem] text-center font-mono-code ${remaining <= 0 ? "text-destructive" : ""}`} data-testid={`kit-remaining-${item.id}-${i}`}>{remaining}/{c.qty}</span>
                <button onClick={() => stepContent(i, -1)} data-testid={`kit-restore-${item.id}-${i}`}
                  className="w-5 h-5 rounded bg-background border border-border grid place-items-center hover:border-primary/50"><Plus className="w-3 h-3" /></button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function AddItemDialog({ open, onOpenChange, onAdd, kits = [] }) {
  const [form, setForm] = useState({ name: "", category: "gear", quantity: 1, unit: "", calibre: "", feeds: "", capacity: "" });
  const [mode, setMode] = useState("single");
  const [kitId, setKitId] = useState("");
  const [saving, setSaving] = useState(false);
  const wasOpen = useRef(false);
  const nameRef = useRef(null);
  useEffect(() => {
    if (open && !wasOpen.current) {
      setForm({ name: "", category: "gear", quantity: 1, unit: "", calibre: "", feeds: "", capacity: "" });
      setMode("single");
      setKitId("");
    }
    wasOpen.current = open;
  }, [open]);

  const showLinks = form.category === "ammo" || form.category === "weapon";
  const selectedKit = kits.find((k) => k.id === kitId);

  const submit = async () => {
    if (!form.name.trim() || saving) return;
    setSaving(true);
    const qty = parseFloat(form.quantity);
    const cap = parseFloat(form.capacity);
    await onAdd({
      name: form.name, category: form.category, unit: form.unit,
      quantity: Number.isNaN(qty) ? 1 : qty,
      calibre: form.calibre.trim(), feeds: form.feeds.trim(),
      capacity: Number.isNaN(cap) ? null : cap,
    });
    // Keep the dialog open for rapid entry: clear name & quantity, keep category/unit/links.
    setForm((f) => ({ ...f, name: "", quantity: 1 }));
    setSaving(false);
    nameRef.current?.focus();
  };

  const submitKit = async () => {
    if (!selectedKit || saving) return;
    setSaving(true);
    // Backend auto-hydrates the per-character contents checklist from the Codex kit.
    await onAdd({ name: selectedKit.name, category: "kit", linked_entity_id: selectedKit.id, quantity: 1, unit: "" });
    setSaving(false);
    setKitId("");
    toast.success(`${selectedKit.name} added — expand it to tick off contents`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-popover max-h-[90vh] overflow-y-auto" data-testid="add-item-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Add items</DialogTitle>
          <DialogDescription>
            {mode === "kit" ? "Drop a ready-made kit onto this character — its contents come along as a tick-off checklist." : "Add as many as you like — the form stays open so you can keep going."}
          </DialogDescription>
        </DialogHeader>

        {kits.length > 0 && (
          <div className="flex rounded-lg border border-border p-0.5 bg-secondary/40" data-testid="add-item-mode">
            <button type="button" onClick={() => setMode("single")} data-testid="add-mode-single"
              className={`flex-1 text-sm rounded-md py-1.5 font-medium transition-colors ${mode === "single" ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}>
              Single item
            </button>
            <button type="button" onClick={() => setMode("kit")} data-testid="add-mode-kit"
              className={`flex-1 text-sm rounded-md py-1.5 font-medium transition-colors inline-flex items-center justify-center gap-1.5 ${mode === "kit" ? "bg-background shadow-sm text-emerald-600 dark:text-emerald-400" : "text-muted-foreground hover:text-foreground"}`}>
              <PackageOpen className="w-4 h-4" /> From a kit
            </button>
          </div>
        )}

        {mode === "kit" ? (
          <div className="space-y-4 py-2" data-testid="add-kit-panel">
            <div className="space-y-1.5">
              <Label>Choose a kit</Label>
              <Select value={kitId} onValueChange={setKitId}>
                <SelectTrigger data-testid="kit-picker-select"><SelectValue placeholder="Pick a kit from your Codex" /></SelectTrigger>
                <SelectContent className="bg-popover">
                  {kits.map((k) => <SelectItem key={k.id} value={k.id}>{k.name} · {(k.contents || []).length} item{(k.contents || []).length !== 1 ? "s" : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {selectedKit ? (
              <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-3 space-y-2" data-testid="kit-preview">
                <p className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                  <PackageOpen className="w-4 h-4 text-emerald-500" /> {selectedKit.name} contains
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {(selectedKit.contents || []).map((c, i) => (
                    <span key={i} className="text-[11px] px-1.5 py-0.5 rounded bg-background border border-border">
                      {c.name}{(c.qty || 1) > 1 ? ` ×${c.qty}` : ""}
                    </span>
                  ))}
                </div>
                <p className="text-[11px] text-muted-foreground">This copies the checklist onto the character. Tick items off as they're used — refill to reset.</p>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">Kits are Codex details whose type is marked as a kit. Pick one above to add its whole contents at once.</p>
            )}
          </div>
        ) : (
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Item name</Label>
            <Input ref={nameRef} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              onKeyDown={(e) => { if (e.key === "Enter" && !showLinks) submit(); }}
              placeholder="Rifle rounds" data-testid="item-name-input" autoFocus />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5 col-span-1">
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="item-category-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-popover">
                  {CATEGORY_ORDER.map((c) => <SelectItem key={c} value={c}>{CATEGORIES[c].label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5 col-span-1">
              <Label>Quantity</Label>
              <Input type="number" value={form.quantity} min={0}
                onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid="item-quantity-input" />
            </div>
            <div className="space-y-1.5 col-span-1">
              <Label>Unit</Label>
              <Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })}
                onKeyDown={(e) => { if (e.key === "Enter" && !showLinks) submit(); }}
                placeholder="rounds" data-testid="item-unit-input" />
            </div>
          </div>
          {showLinks && (
            <div className="rounded-lg border border-border bg-secondary/30 p-3 space-y-3" data-testid="ammo-link-fields">
              <p className="text-[11px] uppercase tracking-widest font-semibold text-muted-foreground">Ammo link (optional)</p>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1.5 col-span-1">
                  <Label className="text-xs">Ammo type</Label>
                  <Input value={form.calibre} onChange={(e) => setForm({ ...form, calibre: e.target.value })}
                    placeholder="5.56mm / arrows / musket balls" data-testid="item-calibre-input" />
                </div>
                <div className="space-y-1.5 col-span-1">
                  <Label className="text-xs">{form.category === "weapon" ? "Uses ammo" : "Feeds weapon"}</Label>
                  <Input value={form.feeds} onChange={(e) => setForm({ ...form, feeds: e.target.value })}
                    placeholder="L85 assault rifle" data-testid="item-feeds-input" />
                </div>
                <div className="space-y-1.5 col-span-1">
                  <Label className="text-xs">Mag size / capacity</Label>
                  <Input type="number" value={form.capacity} min={0}
                    onChange={(e) => setForm({ ...form, capacity: e.target.value })}
                    onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
                    placeholder="30" data-testid="item-capacity-input" />
                </div>
              </div>
              <p className="text-[11px] text-muted-foreground">Rounds per magazine (used to convert "2 mags" ↔ rounds). e.g. an "L85 30-round magazine" = type 5.56mm, feeds L85, mag size 30. A quiver = type arrows, feeds longbow, capacity 24.</p>
            </div>
          )}
        </div>
        )}
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="close-add-item-button">Done</Button>
          {mode === "kit" ? (
            <Button onClick={submitKit} disabled={!selectedKit || saving} data-testid="save-kit-button" className="gap-1.5">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <PackageOpen className="w-4 h-4" />} Add kit
            </Button>
          ) : (
            <Button onClick={submit} disabled={!form.name.trim() || saving} data-testid="save-item-button">Add item</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditItemDialog({ item, onOpenChange, onSave }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (item) setForm({
      name: item.name || "", category: item.category || "gear",
      quantity: item.quantity ?? 1, unit: item.unit || "",
      calibre: item.calibre || "", feeds: item.feeds || "",
      capacity: item.capacity ?? "",
    });
  }, [item]);
  if (!item || !form) return null;
  const showLinks = form.category === "ammo" || form.category === "weapon";
  const save = async () => {
    if (!form.name.trim() || saving) return;
    setSaving(true);
    const qty = parseFloat(form.quantity);
    const cap = parseFloat(form.capacity);
    try {
      await onSave({
        name: form.name, category: form.category, unit: form.unit,
        quantity: Number.isNaN(qty) ? 0 : qty,
        calibre: form.calibre.trim(), feeds: form.feeds.trim(),
        capacity: Number.isNaN(cap) ? null : cap,
      });
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={!!item} onOpenChange={onOpenChange}>
      <DialogContent className="bg-popover max-h-[90vh] overflow-y-auto" data-testid="edit-item-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Edit "{item.name}"</DialogTitle>
          <DialogDescription>Update the item — and link ammo to the weapon it feeds.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Item name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="edit-item-name-input" autoFocus />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5 col-span-1">
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="edit-item-category-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-popover">
                  {CATEGORY_ORDER.map((c) => <SelectItem key={c} value={c}>{CATEGORIES[c].label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5 col-span-1">
              <Label>Quantity</Label>
              <Input type="number" value={form.quantity} min={0}
                onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid="edit-item-quantity-input" />
            </div>
            <div className="space-y-1.5 col-span-1">
              <Label>Unit</Label>
              <Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })}
                placeholder="rounds" data-testid="edit-item-unit-input" />
            </div>
          </div>
          {showLinks && (
            <div className="rounded-lg border border-border bg-secondary/30 p-3 space-y-3" data-testid="edit-ammo-link-fields">
              <p className="text-[11px] uppercase tracking-widest font-semibold text-muted-foreground">Ammo link</p>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1.5 col-span-1">
                  <Label className="text-xs">Ammo type</Label>
                  <Input value={form.calibre} onChange={(e) => setForm({ ...form, calibre: e.target.value })}
                    placeholder="5.56mm / arrows / musket balls" data-testid="edit-item-calibre-input" />
                </div>
                <div className="space-y-1.5 col-span-1">
                  <Label className="text-xs">{form.category === "weapon" ? "Uses ammo" : "Feeds weapon"}</Label>
                  <Input value={form.feeds} onChange={(e) => setForm({ ...form, feeds: e.target.value })}
                    placeholder="L85 assault rifle" data-testid="edit-item-feeds-input" />
                </div>
                <div className="space-y-1.5 col-span-1">
                  <Label className="text-xs">Capacity</Label>
                  <Input type="number" value={form.capacity} min={0}
                    onChange={(e) => setForm({ ...form, capacity: e.target.value })}
                    onKeyDown={(e) => { if (e.key === "Enter") save(); }}
                    placeholder="30" data-testid="edit-item-capacity-input" />
                </div>
              </div>
              <p className="text-[11px] text-muted-foreground">e.g. "L85 30-round magazine" → type 5.56mm, feeds L85, capacity 30. Or a quiver → type arrows, feeds longbow, capacity 24.</p>
            </div>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="cancel-edit-item-button">Cancel</Button>
          <Button onClick={save} disabled={!form.name.trim() || saving} data-testid="save-edit-item-button">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TransferDialog({ item, owners, onOpenChange, onTransfer }) {
  const [target, setTarget] = useState("");
  React.useEffect(() => { setTarget(""); }, [item]);
  const options = owners.filter((o) => o.id !== item?.owner_id);
  return (
    <Dialog open={!!item} onOpenChange={onOpenChange}>
      <DialogContent className="bg-popover" data-testid="transfer-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Transfer "{item?.name}"</DialogTitle>
          <DialogDescription>Move this item to another character or vehicle.</DialogDescription>
        </DialogHeader>
        <div className="py-2 space-y-1.5">
          <Label>Move to</Label>
          <Select value={target} onValueChange={setTarget}>
            <SelectTrigger data-testid="transfer-target-select"><SelectValue placeholder="Choose owner…" /></SelectTrigger>
            <SelectContent className="bg-popover">
              {options.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button onClick={() => onTransfer(target)} disabled={!target} data-testid="confirm-transfer-button" className="gap-2">
            <ArrowRightLeft className="w-4 h-4" /> Transfer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RedistributeDialog({ open, onOpenChange, owners, ammoByCalibre, onRun }) {
  const cals = Object.values(ammoByCalibre);
  const [cal, setCal] = useState("");
  const [donors, setDonors] = useState([]);
  const [recips, setRecips] = useState([]);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const label = ammoByCalibre[cal]?.label || cal;

  React.useEffect(() => {
    if (!open) { setCal(""); setDonors([]); setRecips([]); setPreview(null); }
  }, [open]);

  const isLiving = (o) => !o.kia && !o.is_source;
  const chooseCal = (k) => {
    setCal(k); setPreview(null);
    const holders = ammoByCalibre[k]?.holders || new Set();
    setDonors(owners.filter((o) => o.kia && holders.has(o.id)).map((o) => o.id));
    const recHolders = owners.filter((o) => isLiving(o) && holders.has(o.id)).map((o) => o.id);
    setRecips(recHolders.length ? recHolders : owners.filter((o) => isLiving(o) && o.type === "character").map((o) => o.id));
  };
  const toggle = (setter, list, id) => { setter(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]); setPreview(null); };

  const runPreview = async () => {
    setBusy(true);
    try { setPreview(await onRun({ calibre: label, donor_ids: donors, recipient_ids: recips, apply: false })); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't preview"); }
    finally { setBusy(false); }
  };
  const apply = async () => {
    setBusy(true);
    try {
      await onRun({ calibre: label, donor_ids: donors, recipient_ids: recips, apply: true });
      toast.success(`Redistributed ${label}`);
      onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't redistribute"); }
    finally { setBusy(false); }
  };

  const Chip = ({ o, active, onClick }) => (
    <button type="button" onClick={onClick} data-testid={`redist-chip-${o.id}`}
      className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${active ? "border-primary bg-primary/15 text-primary" : "border-border text-muted-foreground hover:border-primary/50"}`}>
      {o.kia ? "☠ " : ""}{o.name}
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-popover max-w-lg max-h-[90vh] overflow-y-auto" data-testid="redistribute-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2"><ArrowRightLeft className="w-5 h-5 text-primary" /> Redistribute ammo</DialogTitle>
          <DialogDescription>Pool one ammo type from the donors and split it evenly across the recipients.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <div className="space-y-1.5">
            <Label>Ammo type</Label>
            <Select value={cal} onValueChange={chooseCal}>
              <SelectTrigger data-testid="redist-calibre-select"><SelectValue placeholder="Choose ammo…" /></SelectTrigger>
              <SelectContent className="bg-popover">
                {cals.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {cal && (
            <>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Take from (donors)</Label>
                <div className="flex flex-wrap gap-1.5" data-testid="redist-donors">
                  {owners.map((o) => <Chip key={o.id} o={o} active={donors.includes(o.id)} onClick={() => toggle(setDonors, donors, o.id)} />)}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Share out to (recipients)</Label>
                <div className="flex flex-wrap gap-1.5" data-testid="redist-recipients">
                  {owners.filter((o) => !o.is_source).map((o) => <Chip key={o.id} o={o} active={recips.includes(o.id)} onClick={() => toggle(setRecips, recips, o.id)} />)}
                </div>
              </div>
              {preview && (
                <div className="rounded-xl border border-border bg-secondary/30 p-3 space-y-2" data-testid="redist-preview">
                  <p className="text-sm"><span className="font-semibold">{preview.pool}</span> rounds of {preview.calibre} pooled.</p>
                  {preview.pool <= 0 && (
                    <p className="text-xs text-amber-500" data-testid="redist-empty-hint">
                      No rounds to redistribute — pick at least one donor who's carrying {preview.calibre}.
                    </p>
                  )}
                  <div className="space-y-1">
                    {[...preview.donors, ...preview.recipients].map((row) => (
                      <div key={row.owner_id + (row.delta < 0 ? "-d" : "-r")} className="flex items-center justify-between text-xs" data-testid={`redist-row-${row.owner_id}`}>
                        <span className="truncate">{row.owner}</span>
                        <span className="font-mono-code">{row.before} → <span className={row.delta > 0 ? "text-emerald-500" : row.delta < 0 ? "text-destructive" : ""}>{row.after}</span> rds</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="redist-cancel">Cancel</Button>
          {!preview ? (
            <Button onClick={runPreview} disabled={!cal || !recips.length || busy} data-testid="redist-preview-button" className="gap-2">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Preview
            </Button>
          ) : (
            <Button onClick={apply} disabled={busy || preview.pool <= 0} data-testid="redist-apply-button" className="gap-2">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRightLeft className="w-4 h-4" />} Apply
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function ScanDialog({ scan, setScan, onApply }) {
  const toggle = (i) => setScan({ ...scan, suggestions: scan.suggestions.map((s, idx) => idx === i ? { ...s, keep: !s.keep } : s) });
  const keptCount = scan?.suggestions?.filter((s) => s.keep).length || 0;
  return (
    <Dialog open={!!scan} onOpenChange={(v) => !v && setScan(null)}>
      <DialogContent className="bg-popover max-w-lg" data-testid="supply-scan-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" /> Supplies found in your story
          </DialogTitle>
          <DialogDescription>Uncheck anything you don't want, then add the rest to your loadouts.</DialogDescription>
        </DialogHeader>
        <div className="max-h-[50vh] overflow-y-auto space-y-2 py-1">
          {scan?.loading ? (
            <div className="flex flex-col items-center py-10 text-muted-foreground">
              <Loader2 className="w-7 h-7 animate-spin text-primary mb-3" /> Reading your chapters…
            </div>
          ) : scan?.suggestions?.length ? scan.suggestions.map((s, i) => {
            const CatIcon = (CATEGORIES[s.category] || CATEGORIES.misc).icon;
            return (
              <button key={i} onClick={() => toggle(i)} data-testid={`scan-suggestion-${i}`}
                className={`w-full flex items-center gap-3 rounded-lg border p-2.5 text-left transition-colors ${s.keep ? "border-primary/50 bg-primary/5" : "border-border opacity-50"}`}>
                <CatIcon className="w-4 h-4 shrink-0" style={{ color: (CATEGORIES[s.category] || CATEGORIES.misc).color }} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{s.quantity} {s.unit} {s.name}</div>
                  <div className="text-xs text-muted-foreground">→ {s.owner}</div>
                </div>
                {s.keep ? <CheckCircle2 className="w-4 h-4 text-primary" /> : <X className="w-4 h-4 text-muted-foreground" />}
              </button>
            );
          }) : <p className="text-sm text-muted-foreground text-center py-8">No supplies detected.</p>}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setScan(null)}>Cancel</Button>
          <Button onClick={onApply} disabled={scan?.loading || keptCount === 0} data-testid="apply-scan-button" className="gap-2">
            <CheckCircle2 className="w-4 h-4" /> Add {keptCount} to loadouts
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CommandDialog({ cmd, setCmd, onApply }) {
  const [busy, setBusy] = useState(false);
  const toggle = (i) => setCmd({ ...cmd, results: cmd.results.map((r, idx) => idx === i ? { ...r, keep: !r.keep } : r) });
  const kept = cmd?.results?.filter((r) => r.keep).length || 0;
  const apply = async () => { setBusy(true); try { await onApply(); } finally { setBusy(false); } };
  return (
    <Dialog open={!!cmd} onOpenChange={(v) => !v && !busy && setCmd(null)}>
      <DialogContent className="bg-popover max-w-lg" data-testid="command-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Wand2 className="w-5 h-5 text-primary" /> Review changes
          </DialogTitle>
          <DialogDescription>Confirm what to apply to your loadouts. Uncheck anything wrong.</DialogDescription>
        </DialogHeader>
        <div className="max-h-[52vh] overflow-y-auto space-y-2 py-1">
          {cmd?.loading ? (
            <div className="flex flex-col items-center py-10 text-muted-foreground">
              <Loader2 className="w-7 h-7 animate-spin text-primary mb-3" /> Understanding your note…
            </div>
          ) : cmd?.results?.map((r, i) => {
            const CatIcon = (CATEGORIES[r.category] || CATEGORIES.misc).icon;
            const isAdd = r.action === "add";
            const warn = r.status === "low" || r.status === "expended";
            const notFound = r.status === "not_found";
            return (
              <button key={i} onClick={() => toggle(i)} data-testid={`command-result-${i}`}
                className={`w-full flex items-center gap-3 rounded-lg border p-2.5 text-left transition-colors ${r.keep ? "border-primary/50 bg-primary/5" : "border-border opacity-50"}`}>
                <span className={`w-7 h-7 rounded-md flex items-center justify-center shrink-0 font-bold ${isAdd ? "bg-emerald-500/15 text-emerald-500" : "bg-red-500/15 text-red-500"}`}>
                  {isAdd ? <Plus className="w-4 h-4" /> : <Minus className="w-4 h-4" />}
                </span>
                <CatIcon className="w-4 h-4 shrink-0" style={{ color: (CATEGORIES[r.category] || CATEGORIES.misc).color }} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">
                    {isAdd ? "Add" : "Use"} {r.quantity} {r.unit} {r.name}
                  </div>
                  <div className="text-xs text-muted-foreground flex items-center gap-1.5 flex-wrap">
                    <span>→ {r.owner}</span>
                    {!notFound && <span className="text-foreground/70">{r.current_qty} → {r.new_qty}</span>}
                    {r.status === "new_owner" && <span className="text-primary">new owner</span>}
                    {r.status === "new_item" && <span className="text-primary">new item</span>}
                    {r.status === "low" && <span className="text-amber-500 flex items-center gap-0.5"><AlertTriangle className="w-3 h-3" />running low</span>}
                    {r.status === "expended" && <span className="text-destructive flex items-center gap-0.5"><AlertTriangle className="w-3 h-3" />expended</span>}
                    {notFound && <span className="text-destructive">not in loadout</span>}
                  </div>
                </div>
                {r.keep ? <CheckCircle2 className="w-4 h-4 text-primary shrink-0" /> : <X className="w-4 h-4 text-muted-foreground shrink-0" />}
              </button>
            );
          })}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setCmd(null)} disabled={busy}>Cancel</Button>
          <Button onClick={apply} disabled={cmd?.loading || busy || kept === 0} data-testid="apply-command-button" className="gap-2">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Apply {kept} change{kept !== 1 ? "s" : ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
